// app.config.ts (Corrected and Fully Modernized for v20)

import { ApplicationConfig, importProvidersFrom, inject, isDevMode, provideAppInitializer, provideZoneChangeDetection } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { provideRouter } from '@angular/router';
import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideServiceWorker } from '@angular/service-worker';

// --- i18n ---
import { provideTranslateService, TranslateLoader, TranslateService } from '@ngx-translate/core';
import { TranslateHttpLoader, provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { firstValueFrom } from 'rxjs';

// --- PrimeNG ---
import { providePrimeNG } from 'primeng/config';
import { MessageService, ConfirmationService } from 'primeng/api';
import Lara from "@primeng/themes/lara";

// --- Firebase (Modern Standalone API) ---
import { provideFirebaseApp, initializeApp } from '@angular/fire/app';
import { provideAuth, getAuth } from '@angular/fire/auth';
import { provideDatabase, getDatabase } from '@angular/fire/database';
import { provideStorage, getStorage } from '@angular/fire/storage';
import { provideFirestore, getFirestore } from '@angular/fire/firestore';
import { provideMessaging, getMessaging } from '@angular/fire/messaging';
import { getFunctions, provideFunctions } from '@angular/fire/functions';
import { NgxGoogleAnalyticsModule, NgxGoogleAnalyticsRouterModule } from 'ngx-google-analytics';

// --- App Specific ---
import { routes } from './app.routes';
import { CustomerService } from './customer/customer.service';
import { BaseMasterDataService } from './services/base-master-data.service';
import { environment } from '../environments/environment';
import { authInterceptor } from './auth/auth.interceptor';

type Language = 'en' | 'ar';

function getInitialLanguage(): Language {
  const saved = localStorage.getItem('preferredLang');

  if (saved === 'en' || saved === 'ar') {
    return saved;
  }

  return navigator.language.toLowerCase().startsWith('ar') ? 'ar' : 'en';
}

function applyDocumentLanguage(document: Document, lang: Language): void {
  document.documentElement.lang = lang;
  document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
  document.body.classList.toggle('rtl', lang === 'ar');
  document.body.classList.toggle('ltr', lang === 'en');
}

export const appConfig: ApplicationConfig = {
  providers: [
    // Core Angular providers
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    provideAnimationsAsync(),
    provideHttpClient(withInterceptors([authInterceptor]), withFetch()),
    importProvidersFrom(
      NgxGoogleAnalyticsModule.forRoot(environment.firebaseConfig.measurementId),
      NgxGoogleAnalyticsRouterModule.forRoot()
    ),

    // ngx-translate configuration (Modern v17+ approach)
    provideTranslateService({
      fallbackLang: 'en',
      loader: {
        provide: TranslateLoader,
        useClass: TranslateHttpLoader
      }
    }),
    provideTranslateHttpLoader({
      prefix: './assets/i18n/',
      suffix: '.json'
    }),
    provideAppInitializer(() => {
      const translate = inject(TranslateService);
      const document = inject(DOCUMENT);
      const lang = getInitialLanguage();

      applyDocumentLanguage(document, lang);

      return firstValueFrom(translate.use(lang));
    }),

    // PrimeNG services and theme preset
    MessageService,
    ConfirmationService,
    providePrimeNG({
      theme: {
        preset: Lara,
      }
    }),

    // Modern, tree-shakable Firebase providers
    provideFirebaseApp(() => initializeApp(environment.firebaseConfig)),
    provideAuth(() => getAuth()),
    provideStorage(() => getStorage()),
    provideDatabase(() => getDatabase()),
    provideFirestore(() => getFirestore()),
    provideFunctions(() => getFunctions()),
    provideMessaging(() => getMessaging()),

    // Application services
    CustomerService,
    BaseMasterDataService,

    // Service Worker
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerWhenStable:30000'
    }),
  ]
};
